/** Formato de visualización datetime 12h con segundos (Figma DatePicker — hora integrada). */
export const DATETIME_DISPLAY_FORMAT = 'dd/MM/yyyy hh:mm:ss a';

export type Meridiem = 'AM' | 'PM';

/** Anchos inline para segmentos del campo (sin token de ancho en DS; unidades `ch`). */
export const INLINE_SEGMENT_WIDTH = {
  twoDigit: 'ui-w-[2ch]',
  fourDigit: 'ui-w-[4ch]',
} as const;

export interface DateTimeParts {
  day: string;
  month: string;
  year: string;
  hour12: string;
  minute: string;
  second: string;
  meridiem: Meridiem;
}

export type ParsedDateTimeInput =
  | { kind: 'full'; date: Date }
  | { kind: 'date-only'; date: Date }
  | { kind: 'time-only'; date: null; time: Pick<DateTimeParts, 'hour12' | 'minute' | 'second' | 'meridiem'> }
  | { kind: 'invalid' };

export const EMPTY_DATETIME_PARTS: DateTimeParts = {
  day: '',
  month: '',
  year: '',
  hour12: '',
  minute: '',
  second: '',
  meridiem: 'AM',
};

/** Hora por defecto cuando el usuario no elige hora (12:00 AM en formato 12h). */
export const DEFAULT_TIME_PARTS: Pick<DateTimeParts, 'hour12' | 'minute' | 'second' | 'meridiem'> = {
  hour12: '12',
  minute: '00',
  second: '00',
  meridiem: 'AM',
};

const FULL_DATETIME_REGEX =
  /^(\d{1,2})\/(\d{1,2})\/(\d{4})\s+(\d{1,2}):(\d{2}):(\d{2})\s*(AM|PM|am|pm)$/;

const DATE_ONLY_REGEX = /^(\d{1,2})\/(\d{1,2})\/(\d{4})$/;

const TIME_ONLY_REGEX = /^(\d{1,2}):(\d{2}):(\d{2})\s*(AM|PM|am|pm)$/;

export const clampHour12 = (value: number): number => {
  if (Number.isNaN(value)) return 12;
  if (value < 1) return 12;
  if (value > 12) return 12;
  return value;
};

/** Minutos y segundos: 00–59 (estándar reloj; Figma no accesible — ver story). */
export const clampMinuteOrSecond = (value: number): number => {
  if (Number.isNaN(value)) return 0;
  if (value < 0) return 0;
  if (value > 59) return 59;
  return value;
};

export const normalizeMeridiem = (value: string): Meridiem => {
  return value.trim().toUpperCase().startsWith('P') ? 'PM' : 'AM';
};

export const pad2 = (value: number | string): string => {
  const n = typeof value === 'string' ? parseInt(value, 10) : value;
  if (Number.isNaN(n)) return '';
  return String(n).padStart(2, '0');
};

export const dateToParts = (date: Date, showSeconds = true): DateTimeParts => {
  const hours24 = date.getHours();
  const meridiem: Meridiem = hours24 >= 12 ? 'PM' : 'AM';
  const hour12 = hours24 % 12 === 0 ? 12 : hours24 % 12;

  return {
    day: pad2(date.getDate()),
    month: pad2(date.getMonth() + 1),
    year: String(date.getFullYear()),
    hour12: pad2(hour12),
    minute: pad2(date.getMinutes()),
    second: showSeconds ? pad2(date.getSeconds()) : '00',
    meridiem,
  };
};

export const partsToDate = (parts: DateTimeParts, showSeconds = true): Date | null => {
  const day = parseInt(parts.day, 10);
  const month = parseInt(parts.month, 10);
  const year = parseInt(parts.year, 10);
  const hour12 = parseInt(parts.hour12, 10);
  const minute = parseInt(parts.minute, 10);
  const second = showSeconds ? parseInt(parts.second, 10) : 0;

  if (
    Number.isNaN(day) ||
    Number.isNaN(month) ||
    Number.isNaN(year) ||
    Number.isNaN(hour12) ||
    Number.isNaN(minute) ||
    (showSeconds && Number.isNaN(second))
  ) {
    return null;
  }

  if (hour12 < 1 || hour12 > 12) return null;
  if (minute < 0 || minute > 59) return null;
  if (showSeconds && (second < 0 || second > 59)) return null;

  let hours24 = hour12 % 12;
  if (parts.meridiem === 'PM') {
    hours24 += 12;
  }

  const candidate = new Date(year, month - 1, day, hours24, minute, showSeconds ? second : 0, 0);

  if (
    candidate.getFullYear() !== year ||
    candidate.getMonth() !== month - 1 ||
    candidate.getDate() !== day
  ) {
    return null;
  }

  return candidate;
};

export const formatDateTimeForInput = (date: Date | null, showSeconds = true): string => {
  if (!date || Number.isNaN(date.getTime())) return '';
  const parts = dateToParts(date, showSeconds);
  const dateSegment = `${parts.day}/${parts.month}/${parts.year}`;
  const timeSegment = showSeconds
    ? `${parts.hour12}:${parts.minute}:${parts.second} ${parts.meridiem}`
    : `${parts.hour12}:${parts.minute} ${parts.meridiem}`;
  return `${dateSegment} ${timeSegment}`;
};

export interface FormatDisplayDateTimeOptions {
  showSeconds?: boolean;
}

/**
 * Formato de visualización read-only del campo principal (`showTime`).
 * Ejemplo: `15/07/2024 - 12:39 AM` o con segundos `15/07/2024 - 12:39:45 AM`.
 */
export const formatDisplayDateTime = (
  date: Date | null,
  options: FormatDisplayDateTimeOptions = {}
): string => {
  const { showSeconds = true } = options;
  if (!date || Number.isNaN(date.getTime())) return '';
  const parts = dateToParts(date, showSeconds);
  const dateSegment = `${parts.day}/${parts.month}/${parts.year}`;
  const timeSegment = showSeconds
    ? `${parts.hour12}:${parts.minute}:${parts.second} ${parts.meridiem}`
    : `${parts.hour12}:${parts.minute} ${parts.meridiem}`;
  return `${dateSegment} - ${timeSegment}`;
};

/** Formato read-only de un rango con hora en cada extremo. */
export const formatDisplayDateTimeRange = (
  range: { start: Date | null; end: Date | null },
  options: FormatDisplayDateTimeOptions = {}
): string => {
  const { showSeconds = true } = options;
  const startText =
    range.start && !Number.isNaN(range.start.getTime())
      ? formatDisplayDateTime(range.start, { showSeconds })
      : '';
  const endText =
    range.end && !Number.isNaN(range.end.getTime())
      ? formatDisplayDateTime(range.end, { showSeconds })
      : '';

  if (startText && endText) {
    return `${startText} / ${endText}`;
  }
  if (startText) {
    return startText;
  }
  if (endText) {
    return endText;
  }
  return '';
};

export const mergeDateWithTimeParts = (baseDate: Date, time: Pick<DateTimeParts, 'hour12' | 'minute' | 'second' | 'meridiem'>, showSeconds = true): Date => {
  const normalized = {
    hour12: pad2(clampHour12(parseInt(time.hour12 || DEFAULT_TIME_PARTS.hour12, 10))),
    minute: pad2(clampMinuteOrSecond(parseInt(time.minute || DEFAULT_TIME_PARTS.minute, 10))),
    second: pad2(clampMinuteOrSecond(parseInt(time.second || DEFAULT_TIME_PARTS.second, 10))),
    meridiem: time.meridiem ? normalizeMeridiem(time.meridiem) : DEFAULT_TIME_PARTS.meridiem,
  };
  const merged = partsToDate(
    {
      day: pad2(baseDate.getDate()),
      month: pad2(baseDate.getMonth() + 1),
      year: String(baseDate.getFullYear()),
      ...normalized,
    },
    showSeconds
  );
  return merged ?? baseDate;
};

export const mergeTimePartsIntoDate = (
  existing: Date | null,
  time: Pick<DateTimeParts, 'hour12' | 'minute' | 'second' | 'meridiem'>,
  showSeconds = true
): Date | null => {
  const base = existing ?? new Date();
  const normalized = {
    hour12: pad2(clampHour12(parseInt(time.hour12 || DEFAULT_TIME_PARTS.hour12, 10))),
    minute: pad2(clampMinuteOrSecond(parseInt(time.minute || DEFAULT_TIME_PARTS.minute, 10))),
    second: pad2(clampMinuteOrSecond(parseInt(time.second || DEFAULT_TIME_PARTS.second, 10))),
    meridiem: normalizeMeridiem(time.meridiem || DEFAULT_TIME_PARTS.meridiem),
  };
  return mergeDateWithTimeParts(base, normalized, showSeconds);
};

export const parseDateTimeInput = (input: string): ParsedDateTimeInput => {
  const trimmed = input.trim();
  if (!trimmed) return { kind: 'invalid' };

  const fullMatch = trimmed.match(FULL_DATETIME_REGEX);
  if (fullMatch) {
    const [, d, m, y, h, min, sec, mer] = fullMatch;
    const date = partsToDate({
      day: pad2(parseInt(d, 10)),
      month: pad2(parseInt(m, 10)),
      year: y,
      hour12: pad2(clampHour12(parseInt(h, 10))),
      minute: pad2(clampMinuteOrSecond(parseInt(min, 10))),
      second: pad2(clampMinuteOrSecond(parseInt(sec, 10))),
      meridiem: normalizeMeridiem(mer),
    });
    return date ? { kind: 'full', date } : { kind: 'invalid' };
  }

  const dateMatch = trimmed.match(DATE_ONLY_REGEX);
  if (dateMatch) {
    const [, d, m, y] = dateMatch;
    const date = partsToDate({
      ...EMPTY_DATETIME_PARTS,
      day: pad2(parseInt(d, 10)),
      month: pad2(parseInt(m, 10)),
      year: y,
      ...DEFAULT_TIME_PARTS,
    });
    return date ? { kind: 'date-only', date } : { kind: 'invalid' };
  }

  const timeMatch = trimmed.match(TIME_ONLY_REGEX);
  if (timeMatch) {
    const [, h, min, sec, mer] = timeMatch;
    return {
      kind: 'time-only',
      date: null,
      time: {
        hour12: pad2(clampHour12(parseInt(h, 10))),
        minute: pad2(clampMinuteOrSecond(parseInt(min, 10))),
        second: pad2(clampMinuteOrSecond(parseInt(sec, 10))),
        meridiem: normalizeMeridiem(mer),
      },
    };
  }

  return { kind: 'invalid' };
};

export const sanitizeNumericSegment = (raw: string, maxLength = 2): string => {
  return raw.replace(/\D/g, '').slice(0, maxLength);
};

export const sanitizeYearSegment = (raw: string): string => {
  return raw.replace(/\D/g, '').slice(0, 4);
};
